import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

interface AdvanceNoticeIndicatorProps {
  hasAdvanceNotice?: boolean;
  variant?: 'icon' | 'chip';
  showText?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

/* eslint-disable max-lines-per-function */
export const AdvanceNoticeIndicator: React.FC<AdvanceNoticeIndicatorProps> = ({
  hasAdvanceNotice = false,
  variant = 'icon',
  showText = false,
  size = 'default',
  className = ''
}) => {
  if (!hasAdvanceNotice) return null;

  const tooltipText = 'This recall is not serviceable yet.';

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    default: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4'
  };

  // Custom yellow alert icon with black exclamation mark
  const YellowAlertIcon = ({ size }: { size: 'sm' | 'default' | 'lg' }) => {
    const sizeValue = size === 'sm' ? 12 : size === 'default' ? 14 : 16;
    return (
      <svg
        width={sizeValue}
        height={sizeValue}
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={iconSizes[size]}
      >
        {/* Yellow triangle with darker border */}
        <path d='M12 2L2 22H22L12 2Z' fill='#FCD34D' stroke='#F59E0B' strokeWidth='1.5' />
        {/* Black exclamation mark - vertical line */}
        <path d='M12 7V13' stroke='#000000' strokeWidth='2.5' strokeLinecap='round' />
        {/* Black exclamation mark - dot */}
        <circle cx='12' cy='16.5' r='1.5' fill='#000000' />
      </svg>
    );
  };

  if (variant === 'chip') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant='outline'
              className={`inline-flex items-center gap-1 bg-warning/10 text-warning border-warning/30 ${
                sizeClasses[size]
              }
              } ${className}`}
            >
              <YellowAlertIcon size={size} />
              {showText && <span>Advance Notice</span>}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className='z-10'>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={
              'inline-flex items-center justify-center ' + `transition-colors ${className}`
            }
            aria-label='Advance notice recall'
          >
            <YellowAlertIcon size={size} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='z-10'>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
